import type { WorkflowContent } from "../../../api/content/authoring/processContentDecoders";
import { VALUE_KINDS, type ValueKind } from "../../../api/content/authoring/selectorDecoders";
import type { StepPorts } from "../../../api/content/processDefinitions";
import { isStepUse, referenceOf, type Binding, type WorkflowStepEntry } from "./workflowDraft";

/**
 * What a step takes and gives in a Workflow (W-6, P-13; authoring-ui.md AU-6): its input
 * slots, which a Workflow binds, and its output parts with the value kinds each may hold,
 * as module B serves them with each step version (F102 CP-04). The graph editor offers and
 * refuses bindings with this, as the server's check does (`workflow_rules.py`): the source
 * must be an earlier step, and the input must accept one of the part's kinds.
 */

/** Ports by the pinned version a Workflow step names; a version not read yet is missing. */
export type PortCatalogue = ReadonlyMap<string, StepPorts>;

/** A placement's ports; none for an abstract step, whose definition the nesting Workflow binds (ADR-0043 D1). */
export function placementPorts(ports: PortCatalogue, step: WorkflowStepEntry): StepPorts | undefined {
  const reference = referenceOf(step);
  return reference === null ? undefined : ports.get(reference);
}

/**
 * A sub-workflow takes no bindings here; its exports are its output parts (P-13). An export
 * whose kinds depend on what a use binds, an abstract step's or one a binding use passes on,
 * however deeply nested, may be any kind here: module B checks the wire against what the
 * bindings give when the draft is saved (ADR-0043 D6). The version's ports can't tell it.
 */
export function subWorkflowPorts(workflow: WorkflowContent, inner: PortCatalogue): StepPorts {
  const outputs: Record<string, readonly ValueKind[]> = {};
  for (const [name, exported] of Object.entries(workflow.exports)) {
    const source = workflow.steps.find((step) => step.key === exported.from_step);
    const kinds = source === undefined ? undefined : placementPorts(inner, source)?.outputs?.[exported.part];
    const bound = source !== undefined && ("accepts" in source || (bindsDefinitions(source) && (kinds === undefined || kinds.length === 0)));
    outputs[name] = bound ? VALUE_KINDS : (kinds ?? []);
  }
  return { inputs: {}, outputs };
}

function bindsDefinitions(step: WorkflowStepEntry): boolean {
  return "workflow" in step && Object.keys(step.definitions ?? {}).length > 0;
}

export type BindRefusal ="sameStep" | "notEarlier" | "noSuchStep" | "noSuchSlot" | "noSuchPart" | "kindsDoNotFit" | "unknownPorts";

/** Every refusal, for their texts. */
export const BIND_REFUSALS: readonly BindRefusal[] = ["sameStep", "notEarlier", "noSuchStep", "noSuchSlot", "noSuchPart", "kindsDoNotFit", "unknownPorts"];

/** Why `from` cannot fill input `slot` of step `key`, or `null` if it can. */
export function bindRefusal(definition: WorkflowContent, ports: PortCatalogue, key: string, slot: string, from: Binding): BindRefusal | null {
  if (from.from_step === key) {
    return "sameStep";
  }
  const targetIndex = definition.steps.findIndex((step) => step.key === key);
  const sourceIndex = definition.steps.findIndex((step) => step.key === from.from_step);
  const target = definition.steps[targetIndex];
  const source = definition.steps[sourceIndex];
  if (target === undefined || source === undefined || !isStepUse(target)) {
    return "noSuchStep";
  }
  if (sourceIndex > targetIndex) {
    return "notEarlier";
  }
  const targetPorts = ports.get(target.step);
  const sourceOutputs = placementPorts(ports, source)?.outputs;
  if (targetPorts === undefined || sourceOutputs == null) {
    return "unknownPorts";
  }
  const input = targetPorts.inputs[slot];
  if (input === undefined) {
    return "noSuchSlot";
  }
  const kinds = sourceOutputs[from.part];
  if (kinds === undefined) {
    return "noSuchPart";
  }
  return kinds.some((kind) => input.accepts.includes(kind)) ? null : "kindsDoNotFit";
}

/** Every earlier part that may fill an input, in step order: what the input's menu offers. */
export function bindableSources(definition: WorkflowContent, ports: PortCatalogue, key: string, slot: string): readonly Binding[] {
  const sources: Binding[] = [];
  for (const step of definition.steps) {
    if (step.key === key) {
      break;
    }
    for (const part of Object.keys(placementPorts(ports, step)?.outputs ?? {})) {
      const from = { from_step: step.key, part };
      if (bindRefusal(definition, ports, key, slot, from) === null) {
        sources.push(from);
      }
    }
  }
  return sources;
}

/** Every step's inputs that could take a part: "Inputs that do" in a refusal. */
export function inputsAccepting(definition: WorkflowContent, ports: PortCatalogue, kinds: readonly ValueKind[]): readonly { readonly key: string; readonly slot: string }[] {
  return definition.steps.flatMap((step) =>
    Object.entries(placementPorts(ports, step)?.inputs ?? {})
      .filter(([, input]) => input.accepts.some((kind) => kinds.includes(kind)))
      .map(([slot]) => ({ key: step.key, slot })),
  );
}

/** Every part a step of the Workflow gives, for the exports panel. */
export function workflowParts(definition: WorkflowContent, ports: PortCatalogue): readonly { readonly from: Binding; readonly kinds: readonly ValueKind[] }[] {
  return definition.steps.flatMap((step) => Object.entries(placementPorts(ports, step)?.outputs ?? {}).map(([part, kinds]) => ({ from: { from_step: step.key, part }, kinds })));
}
